import { useEffect } from 'react'
import { X } from '@/components/icons'
import { Button } from './ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import { AirportDetailContent } from './AirportDetailView'
import { track, EVENTS } from '@/utils/analytics'

interface AirportDetailDrawerProps {
  /** Airport to show; null keeps the drawer unmounted content-wise. */
  icao: string | null
  isOpen: boolean
  onClose: () => void
}

// Bottom-sheet airport intel viewer (weather, overview, suppliers, hotels,
// customs, runways, fuel, restrictions). Replaces navigating to the
// /airport/$icao page from in-app taps so the underlying screen (e.g. an
// open flight form) stays mounted; the page remains for deep links.
export function AirportDetailDrawer({ icao, isOpen, onClose }: AirportDetailDrawerProps) {
  useEffect(() => {
    if (isOpen && icao) {
      track(EVENTS.AIRPORT_DETAIL_VIEWED, { icao, surface: 'drawer' })
    }
  }, [isOpen, icao])

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="bg-surface-L0 rounded-tl-2xl rounded-tr-2xl shadow-lg max-h-[85dvh] flex flex-col">
        <DrawerHeader className="flex flex-row items-center justify-between p-4">
          <DrawerTitle className="text-lg font-bold tracking-[-0.5px] text-content-primary-bold">
            {icao ?? ''}
          </DrawerTitle>
          <Button
            variant="secondary"
            size="icon"
            roundness="round"
            className="size-8 shrink-0"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </DrawerHeader>

        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-[calc(1rem+var(--safe-bottom))]"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {icao && <AirportDetailContent icao={icao} showFbos={false} />}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
